import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sun, CheckCircle, Clock, Mail, ArrowRight, Shield, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const VendorRegistrationSuccess = () => {
    return (
        <div className="min-h-screen pt-20 flex items-center justify-center py-12 px-4 bg-gradient-to-b from-green-50 to-background dark:from-green-950/20">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-lg text-center"
            >
                {/* Success Animation */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="w-24 h-24 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                    >
                        <CheckCircle className="w-12 h-12 text-green-600" />
                    </motion.div>
                </motion.div>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Link to="/" className="inline-flex items-center gap-2 mb-4">
                        <Sun className="w-8 h-8 text-primary" />
                        <span className="text-xl font-bold"><span className="solar-text-gradient">Solar</span>Savers</span>
                    </Link>

                    <h1 className="text-2xl md:text-3xl font-bold text-green-700 dark:text-green-400 mb-2">
                        Application Submitted Successfully!
                    </h1>
                    <p className="text-muted-foreground">
                        Thank you for registering as a vendor on SolarSavers
                    </p>
                </motion.div>

                {/* Info Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-card rounded-2xl border shadow-lg p-6 md:p-8 mt-8"
                >
                    {/* Status */}
                    <div className="flex items-center justify-center gap-3 mb-6 pb-6 border-b">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="text-left">
                            <p className="font-semibold">Application Under Review</p>
                            <p className="text-sm text-muted-foreground">Usually takes 1-2 business days</p>
                        </div>
                    </div>

                    {/* What happens next */}
                    <div className="text-left space-y-4 mb-6">
                        <h3 className="font-semibold text-lg">What happens next?</h3>

                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-primary">1</span>
                                </div>
                                <div>
                                    <p className="font-medium">Document Verification</p>
                                    <p className="text-sm text-muted-foreground">Our team will verify all your submitted documents and certifications</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-primary">2</span>
                                </div>
                                <div>
                                    <p className="font-medium">Background Check</p>
                                    <p className="text-sm text-muted-foreground">We'll validate your business details and credentials</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-primary">3</span>
                                </div>
                                <div>
                                    <p className="font-medium">Approval Notification</p>
                                    <p className="text-sm text-muted-foreground">Once approved, you'll receive an email with login instructions</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notification Banner */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-left">
                                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                    You will be notified via email
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                    Please check your inbox (and spam folder) for updates on your application status.
                                    We'll send you a confirmation email once your account is approved.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Benefits */}
                    <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b">
                        <div className="text-center">
                            <div className="w-10 h-10 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                <Shield className="w-5 h-5 text-primary" />
                            </div>
                            <p className="text-xs font-medium">Verified Seller Badge</p>
                        </div>
                        <div className="text-center">
                            <div className="w-10 h-10 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            <p className="text-xs font-medium">Access to 1L+ Customers</p>
                        </div>
                        <div className="text-center">
                            <div className="w-10 h-10 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                <Award className="w-5 h-5 text-primary" />
                            </div>
                            <p className="text-xs font-medium">Subsidy Support</p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                        <Button asChild className="w-full btn-primary gap-2">
                            <Link to="/login">
                                Go to Login
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Button>
                        <Button asChild variant="outline" className="w-full">
                            <Link to="/">
                                Back to Home
                            </Link>
                        </Button>
                    </div>
                </motion.div>

                {/* Help Text */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="mt-6 text-sm text-muted-foreground"
                >
                    Need help? Contact us at{' '}
                    <a href="mailto:vendor-support@solarsavers.in" className="text-primary hover:underline">
                        vendor-support@solarsavers.in
                    </a>
                </motion.p>
            </motion.div>
        </div>
    );
};

export default VendorRegistrationSuccess;
